import React, { useState, useEffect } from 'react';
import { ImageOff, LineChart } from 'lucide-react';
import Spinner from '../Spinner';
import styles from './PnL.module.sass';

interface PnLTransactionsProps {
  loadingState: boolean;
  pnlList: PnLData[];
  selectedWallet: string;
  activeIndex: number;
  isPaused: boolean;
  onSetPaused: (paused: boolean) => void;
  onNavigateCards: (direction: 'next' | 'prev') => void;
}

interface PnLData {
  token_address: string;
  wallet_address: string;
  symbol: string;
  chain: string;
  realized_profit_usd: string;
  realized_profit_percentage: number;
  avg_buy_price_usd: string;
  avg_sell_price_usd: string;
  total_usd_invested: string;
  logo: string;
  name: string;
  decimals: string;
  total_tokens_bought: string;
  total_tokens_sold: string;
  total_buys: number;
  total_sells: number;
  total_sold_usd: string;
  count_of_trades: number;
  avg_cost_of_quantity_sold: string;
  possible_spam: boolean;
}

const TokenImage = ({ url, symbol }: { url?: string; symbol: string }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [url]);

  if (!url || hasError) {
    return (
      <div className={styles.tokenImage}>
        <ImageOff size={14} className="text-gray-400" />
      </div>
    );
  }

  return (
    <div className={styles.tokenImage}>
      <img
        src={url}
        alt={`${symbol} token`}
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const PnLTransactions: React.FC<PnLTransactionsProps> = ({
  loadingState,
  pnlList,
  selectedWallet,
  activeIndex,
  isPaused,
  onSetPaused,
  onNavigateCards
}) => {
  const formatUSD = (value: string) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const getMetricsForPnL = (pnl: PnLData) => {
    const getAverageStatus = () => {
      const avgBuy = parseFloat(pnl.avg_buy_price_usd);
      const avgSell = parseFloat(pnl.avg_sell_price_usd);
      
      if (pnl.avg_sell_price_usd === "0" || avgBuy === 0) {
        return "yellow";
      }
      return avgSell > avgBuy ? "green" : "red";
    };

    const averageStatus = getAverageStatus();
    const priceDiff = pnl.avg_sell_price_usd !== "0" ? 
      ((parseFloat(pnl.avg_sell_price_usd) - parseFloat(pnl.avg_buy_price_usd)) / 
      parseFloat(pnl.avg_buy_price_usd) * 100).toFixed(1) : null;

    return {
      "Realized P/L": {
        value: formatUSD(pnl.realized_profit_usd),
        change: `${pnl.realized_profit_percentage.toFixed(2)}%`,
        status: parseFloat(pnl.realized_profit_usd) >= 0 ? "green" : "red"
      },
      "Tokens Bought": {
        value: parseFloat(pnl.total_tokens_bought).toFixed(4),
        change: null,
        status: "yellow"
      },
      "Avg Buy": {
        value: formatUSD(pnl.avg_buy_price_usd),
        change: null,
        status: averageStatus
      },
      "Avg Sell": {
        value: pnl.avg_sell_price_usd === "0" ? "N/A" : formatUSD(pnl.avg_sell_price_usd),
        change: priceDiff ? `${priceDiff}%` : null,
        status: averageStatus
      },
      "Total Value": {
        value: formatUSD(pnl.total_usd_invested),
        change: null,
        status: "yellow"
      },
      "Total Sold": {
        value: formatUSD(pnl.total_sold_usd),
        change: null,
        status: "yellow"
      }
    };
  };

  const getCardStyles = (index: number): React.CSSProperties => {
    const position = (index - activeIndex + pnlList.length) % pnlList.length;
    const totalCards = pnlList.length;
    
    const isLeft = position > totalCards / 2;
    const positionFromActive = isLeft ? position - totalCards : position;
    
    let transform = '';
    let zIndex = totalCards - Math.abs(positionFromActive);
    let opacity = 1;
    let boxShadow = 'none';
    let filter = 'none';
    
    if (position === 0) {
      transform = 'translateX(0) scale(1.02)';
      opacity = 1;
      boxShadow = '0 4px 20px rgba(115, 246, 255, 0.1)';
      zIndex = totalCards + 1;
    } else {
      const direction = isLeft ? -1 : 1;
      const offset = Math.min(Math.abs(positionFromActive), 3);
      // Increased offset percentage and reduced scale factor
      transform = `translateX(${direction * (15 + (offset - 1) * 5)}%) scale(${1 - offset * 0.05})`;
      opacity = Math.max(0.5, 1 - offset * 0.2);
      filter = 'brightness(0.95)';
    }
  
    return {
      position: 'absolute' as const,
      transform,
      zIndex,
      opacity,
      boxShadow,
      filter,
      transition: 'all 0.4s ease-in-out'
    };
  };

  if (loadingState) {
    return (
      <div className="flex justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (!pnlList.length) {
    return (
      <div className={styles.emptyStateContainer}>
        <div className={styles.emptyStateIcon}>
          <LineChart size={28} />
        </div>
        <h3 className={styles.emptyStateTitle}>No PnL Data</h3>
        <p className={styles.emptyStateDescription}>
          No profit and loss data is currently available for wallet ending in {selectedWallet ? selectedWallet.slice(-4) : '****'}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <div 
        id="pnl-transactions"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "800px",
          margin: "0 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "320px",
          minHeight: "320px",
          overflow: "hidden",
          padding: "0 40px"  // Added padding for better visual balance
        }}
        onMouseEnter={() => onSetPaused(true)}
        onMouseLeave={() => onSetPaused(false)}
      >
        {pnlList.map((pnl: PnLData, index: number) => {
          const position = (index - activeIndex + pnlList.length) % pnlList.length;
          const handleClick = () => {
            if (position === 1) onNavigateCards('next');
            if (position === pnlList.length - 1) onNavigateCards('prev');
          };

          return (
            <div
              key={`${pnl.token_address}-${index}`}
              className={styles.container}
              style={{
                width: '600px',  // Fixed width instead of percentage
                maxWidth: '80%', // Prevent too wide on smaller screens
                height: '320px',
                ...getCardStyles(index)
              }}
              onClick={handleClick}
            >
              {/* Card content remains the same */}
              <div className={styles.header}>
                <div className={styles.headerContent}>
                  <div className={styles.textContainer}>
                    <h2>{pnl.symbol} / {pnl.chain.toUpperCase()}</h2>
                    <span className={styles.walletAddress}>
                      ****{selectedWallet.slice(-4)}
                    </span>
                  </div>
                  <TokenImage url={pnl.logo} symbol={pnl.symbol} />
                </div>
              </div>

              <div className={styles.content}>
                <div className={styles.metricsGrid}>
                  {Object.entries(getMetricsForPnL(pnl)).map(([key, data], idx) => (
                    <div key={`${key}-${idx}`} className={styles.metricItem}>
                      <div className={styles.metricTitle}>{key}</div>
                      <div className={styles.metricValue}>
                        <span className={`${styles.indicator} ${styles[data.status]}`} />
                        {data.value}
                        {data.change && (
                          <span className={`
                            ${styles.changeIndicator}
                            ${data.change.startsWith('-') ? styles.negative : styles.positive}
                          `}>
                            {data.change}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PnLTransactions;